<template>
  <main
    class="tee-time-check-in"
    :class="{ 'reservation-loaded': reservations, 'early-or-late': earlyForCheckIn || lateForCheckIn }"
  >
    <section class="search-reservation" @click.shift="testMode = true">
      <div class="search-reservation__fields">
        <div class="search-field">
          <ax-label>Where are you playing?</ax-label>
          <ax-select
            v-model="selectedCourse"
            :select-options="courses"
            :disabled="!courses || (courses && courses.length == 0)"
            :class="{ error: $v.selectedCourse.$error }"
          />
        </div>
        <div class="search-field">
          <ax-input
            v-model="confirmationNumber"
            placeholder="Confirmation #"
            :class="{ error: $v.confirmationNumber.$error }"
            inputmode="numeric"
            @enterKey="fetchReservations"
          />
        </div>
        <div class="search-field">
          <ax-input
            v-model="lastName"
            placeholder="Last Name"
            :class="{ error: $v.lastName.$error }"
            @enterKey="fetchReservations"
          />
        </div>
        <ax-button
          class="search-button"
          button-type="primary"
          @click="fetchReservations"
          :disabled="fetchingReservations || (!testMode && (earlyForCheckIn || lateForCheckIn))"
          :label="labels.Buttons.Search"
        ></ax-button>
      </div>
    </section>
    <div id="hero" class="full-hero" ref="hero">
      <div id="hero-image">
        <div
          id="dynamic-hero"
          :style="`background-image: url('${selectedCourse.photo}');`"
          class="active"
          v-if="selectedCourse"
        ></div>
      </div>
      <h1>Tee Time Check-In</h1>
    </div>
    <loading-animation type="spinner" v-show="fetchingReservations"></loading-animation>
    <transition name="fade--up" leave-active-class="instant-hide">
      <section class="search-results" ref="searchResults" v-if="reservations">
        <template v-for="res in reservations">
          <div class="search-results__header">
            <div>{{ reservationDate.format('LL') }}</div>
            <div>{{ `${reservationFirstName} ${reservationLastName}` }}</div>
          </div>
          <div class="search-results__list">
            <div class="player-slot" v-for="slot in playerSlots(res)">
              <div class="player-slot__course">{{ reservationCourse }}</div>
              <div class="player-slot__date">{{ reservationDate.format('MMM DD, YYYY') }}</div>
              <div class="player-slot__time">{{ res.Time }}</div>
              <div class="player-slot__number">
                <template v-if="slot.State === 'CheckedIn'">{{ slot.FirstName }} {{ slot.LastName }}</template>
                <template v-else>Player {{ slot.ResourceNumber }}</template>
              </div>
              <div class="player-slot__action">
                <ax-button
                  v-if="slot.State === 'NotCheckedIn' || slotInCart(slot)"
                  button-type="primary"
                  :checked="slotSelected(slot) || slotInCart(slot)"
                  :show-checkbox="true"
                  :label="`Check-In | ${$options.filters.currency(reservationPrice)}`"
                  :show-loader="addingSlots.includes(slot.Id) || removingSlots.includes(slot.Id)"
                  :disabled="addingSlots.includes(slot.Id) || removingSlots.includes(slot.Id)"
                  @click="toggleSlot(res, slot, false)"
                ></ax-button>
                <template v-else-if="slot.State === 'PendingCheckin'">
                  <ax-button label="Pending Check-In" class="slot--pendingCheckIn" :disabled="true"></ax-button>
                  <p class="pendingCheckInMessage">
                    This player is being checked in.
                    <br />
                    <a @click="forceCheckIn(res, slot)">Check In</a> this slot anyway.
                  </p>
                </template>
                <ax-button
                  v-else-if="slot.State === 'CheckedIn'"
                  label="Checked In"
                  :disabled="true"
                  class="slot--checkedIn"
                ></ax-button>
              </div>
            </div>
          </div>
        </template>
        <transition name="fade--up" leave-active-class="instant-hide">
          <div
            class="search-results__continue"
            v-if="cartHasSlots || (checkInSlots.length > 0 && addingSlots.length < 1)"
          >
            <ax-button @click="continueCheckIn" size="LG" :label="labels.Buttons.Next"></ax-button>
          </div>
        </transition>
        <transition name="fade--up" leave-active-class="instant-hide">
          <div class="search-results__continue" v-if="errorMessage != null">
            <div class="text-danger" v-html="errorMessage"></div>
          </div>
        </transition>
      </section>
    </transition>
    <transition name="fade--up" leave-active-class="instant-hide">
      <section class="no-result" v-if="!reservations && noResult">
        <h2 v-html="labels.NoReservationTitle"></h2>
        <p v-html="labels.NoReservationText"></p>
      </section>
    </transition>
    <transition name="fade--up" leave-active-class="instant-hide">
      <section class="early-or-late" v-if="reservationQueryDate && !reservations && earlyForCheckIn">
        <h2 v-html="labels.TooEarlyTitle"></h2>
        <p v-html="labels.TooEarlyText"></p>
        <ax-button @click="resetSearch" :label="labels.Buttons.SearchNew"></ax-button>
      </section>
    </transition>
    <transition name="fade--up" leave-active-class="instant-hide">
      <section class="early-or-late" v-if="reservationQueryDate && !reservations && lateForCheckIn">
        <h2 v-html="labels.TooLateTitle"></h2>
        <p v-html="labels.TooLateText"></p>
        <ax-button @click="resetSearch" :label="labels.Buttons.SearchNew"></ax-button>
      </section>
    </transition>
    <section class="help-content" :data-player-count="playerCount" :style="{ marginTop: `${helpTopMargin}px` }">
      <portal-target name="teetime_checkin_help_content"></portal-target>
    </section>
  </main>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import numeral from 'numeral'
import LoadingAnimation from '@/components/loading-animation'
import { mapActions, mapGetters } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import AxButton from '@freestyle/ax/AxButton.vue'
import AxSelect from '@freestyle/ax/AxSelect.vue'
import AxInput from '@freestyle/ax/AxInput.vue'
import AxLabel from '@freestyle/ax/AxLabel.vue'

export default {
  name: 'tee-time-check-in',
  components: {
    LoadingAnimation,
    AxButton,
    AxSelect,
    AxInput,
    AxLabel,
  },
  data() {
    return {
      addingSlots: [],
      cartHasSlots: false,
      checkInProduct: null,
      checkInSlots: [],
      confirmationNumber: null,
      courseId: null,
      courses: [],
      helpTopMargin: 0,
      fetchingReservations: false,
      lastName: null,
      noResult: false,
      playerCount: null,
      removingSlots: [],
      reservations: null,
      selectedReservation: null,
      reservationCourse: null,
      reservationDate: null,
      reservationFirstName: null,
      reservationLastName: null,
      reservationPrice: null,
      reservationQueryDate: null,
      resizeId: 0,
      selectedCourse: null,
      testMode: false,
      errorMessage: null,
    }
  },
  validations() {
    return {
      selectedCourse: {
        required,
      },
      lastName: {
        required,
      },
      confirmationNumber: {
        required,
      },
    }
  },
  mounted() {
    this.testMode = this.$route.query.testMode || false
    this.courseId = this.$route.query.courseId ? parseInt(this.$route.query.courseId) : null

    this.fetchCourses()
    this.fetchCheckinProduct()
    this.fetchCartItems()

    this.reservationQueryDate = this.$route.query.teeTimeDate
      ? moment(this.$route.query.teeTimeDate).startOf('day')
      : moment()

    this.confirmationNumber = this.$route.query.confirmationNumber ? this.$route.query.confirmationNumber : null

    this.lastName = this.$route.query.lastName || null

    window.addEventListener('resize', () => {
      clearTimeout(this.resizeId)
      this.resizeId = setTimeout(this.doneResizingWindow, 500)
    })
  },
  computed: {
    config() {
      return {
        baseURL: '/',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'Cache-Control': 'no-cache',
        },
      }
    },
    checkinDateAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Reservation Date')
    },
    checkinTimeAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Reservation Time')
    },
    checkinHolesAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Reservation Holes')
    },
    checkinReservationPriceAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Reservation Price')
    },
    checkinEntitlementIdAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Entitlement Id')
    },
    checkinCourseIdAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Course Id')
    },
    checkinSlotNumberAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Slot Nbr')
    },
    checkinSlotNameAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Slot Name')
    },
    checkinSlotIdAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Slot Id')
    },
    checkinFeeIdAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Fee Id')
    },
    checkinFeeNameAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Fee Name')
    },
    checkinFeeOptionsAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Fee Options')
    },
    checkinForceCheckinAttribute() {
      return this.checkInProduct.ProductAttributes.find((attr) => attr.Name === 'Force Checkin')
    },
    checkinQuantityAttribute() {
      if (this.checkInProduct) {
        return {
          Name: 'Quantity',
          Id: `addtocart_${this.checkInProduct.ProductId}.EnteredQuantity`,
          IsQuantity: true,
          SelectedValue: 1,
        }
      }

      return null
    },
    checkinPriceAttribute() {
      if (this.selectedReservation && this.checkInProduct) {
        return {
          Name: 'Price',
          Id: `addtocart_${this.checkInProduct.ProductId}.CustomerEnteredPrice`,
          IsCustomerEnteredPrice: true,
          SelectedValue: `${this.selectedReservation.Price}`, // While the time slot price is default, this may get overwritten with a slot-specific price when a slot is checked in
        }
      }
      return null
    },
    checkInAttributes() {
      let attrs = null
      if (this.selectedReservation && this.checkInProduct) {
        attrs = [
          this.checkinDateAttribute,
          this.checkinTimeAttribute,
          this.checkinHolesAttribute,
          this.checkinQuantityAttribute,
          this.checkinPriceAttribute,
          this.checkinFeeIdAttribute,
          this.checkinFeeNameAttribute,
          this.checkinFeeOptionsAttribute,
          this.checkinReservationPriceAttribute,
          this.checkinEntitlementIdAttribute,
          this.checkinCourseIdAttribute,
          this.checkinSlotNumberAttribute,
          this.checkinSlotNameAttribute,
          this.checkinSlotIdAttribute,
          this.checkinForceCheckinAttribute,
        ]
      }

      return attrs
    },

    earlyForCheckIn() {
      if (this.testMode) {
        return false
      }

      return this.reservationQueryDate ? moment().startOf('day').diff(this.reservationQueryDate, 'days') < 0 : true
    },

    labels() {
      return {
        NoReservationTitle: checkinLabels.NoReservationTitle,
        NoReservationText: checkinLabels.NoReservationText,
        TooEarlyTitle: checkinLabels.TooEarlyTitle,
        TooEarlyText: this.reservationQueryDate
          ? checkinLabels.TooEarlyText.replace('${DateOfPlay}', this.reservationQueryDate.format('MMMM Do, YYYY'))
          : checkinLabels.TooEarlyText,
        TooLateTitle: checkinLabels.TooLateTitle,
        TooLateText: this.reservationQueryDate
          ? checkinLabels.TooLateText.replace('${DateOfPlay}', this.reservationQueryDate.format('MMMM Do, YYYY'))
          : checkinLabels.TooLateText,
        Buttons: checkinLabels.Buttons,
      }
    },

    lateForCheckIn() {
      if (this.testMode) {
        return false
      }

      return this.reservationQueryDate ? moment().diff(this.reservationQueryDate, 'days') > 0 : true
    },
    ...mapGetters({
      cart: 'cart/cart',
    }),
  },
  methods: {
    playerSlots(reservation) {
      if (reservation && reservation.Usage) {
        return reservation.Usage.UsageDetails
      }

      return null
    },

    forceCheckIn(reservation, slot) {
      slot.State = 'NotCheckedIn'
      this.toggleSlot(reservation, slot, true)
    },

    async addSlotToCart(slot) {
      this.errorMessage = null
      const slotDetails = {
        addToLocalCart: true,
        productAttributes: this.checkInAttributes,
        productId: this.checkInProduct.ProductId,
        productName: this.checkInProduct.ProductName,
      }

      this.addingSlots = [...this.addingSlots, slot.Id]
      const response = await this.addToCart(slotDetails)

      const filteredSlots = this.addingSlots.filter((s) => s !== slot.Id)
      this.addingSlots = filteredSlots

      if (response.success === true) {
        this.$set(slot, 'shoppingCartItemId', response.shoppingCartItemId)
        this.selectedReservation = null
      } else {
        this.errorMessage = response.message[0]
        return false
      }
      return true
    },

    autoSearch() {
      if (
        this.courseId &&
        this.confirmationNumber &&
        this.lastName &&
        !this.fetchingReservations &&
        !this.earlyForCheckIn &&
        !this.lateForCheckIn
      ) {
        this.fetchReservations()
      }
    },

    continueCheckIn() {
      window.location = '/onepagecheckout'
    },

    doneResizingWindow() {
      this.setHelpContentMargin()
    },

    async fetchCheckinProduct() {
      const productUrl = `/api/products/productattributes/${checkinSettings.CheckIn.TeeTimeProductId}`
      axios
        .get(productUrl, this.config)
        .then((response) => {
          this.checkInProduct = response.data
        })
        .catch((errorResponse) => {
          console.log(`ERROR GETTING CHECKIN PRODUCT: ${errorResponse}`)
        })
    },

    async fetchCourses() {
      const coursesUrl = `/api/category/${checkinSettings.CheckIn.GolfCategoryId}`
      axios
        .get(coursesUrl, this.config)
        .then((response) => {
          let data = response.data

          this.courses = data.ProductDetails.map((courseProductDetail) => {
            let courseName = courseProductDetail.Name

            let course = {
              name: courseName,
              text: courseName.substr(courseName.indexOf('-') + 1).trim(),
              value: courseProductDetail.Sku,
              productId: courseProductDetail.Id,
              photo: courseProductDetail.DefaultPictureModel.FullSizeImageUrl,
            }

            if (this.courseId && this.courseId.toString() === course.value) {
              this.selectedCourse = course
              this.autoSearch()
            }
            return course
          })
        })
        .catch((error) => {
          console.log(`Error getting courses: ${error}`)
        })
    },

    fetchReservations() {
      const fetchReservationsUrl = `/api/golf/reservation`

      this.$v.$touch()

      if (this.$v.$error) {
        return
      }

      const reservationQuery = {
        reservationDate: this.reservationQueryDate.format('M/D/YYYY'),
        confirmationNumber: this.confirmationNumber,
        lastName: this.lastName,
        firstName: null,
        courseProductId: this.selectedCourse.productId,
      }

      this.courseId = this.selectedCourse.value

      this.reservation = null
      this.fetchingReservations = true
      this.helpTopMargin = 0
      this.noResult = false

      axios
        .post(fetchReservationsUrl, reservationQuery, this.config)
        .then((response) => {
          if (response.data.length == 0) {
            this.noResult = true
            this.reservations = null
          } else {
            this.reservations = response.data
            const firstReservation = this.reservations[0]
            this.reservationDate = moment(firstReservation.TeeTimeDate)
            this.reservationPrice = firstReservation.Price
            const firstSlot = firstReservation.Usage.UsageDetails[0]
            this.playerCount = firstReservation.Usage.UsageDetails.length
            this.reservationFirstName = firstSlot.FirstName
            this.reservationLastName = firstSlot.LastName
            this.reservationCourse = this.selectedCourse.name

            setTimeout(() => {
              this.setHelpContentMargin()
            }, 50)
          }
        })
        .catch((error) => {
          console.log(`Error retrieving reservation: ${error}`)
          this.noResult = true
        })
        .finally(() => {
          this.fetchingReservations = false
        })
    },

    setHelpContentMargin() {
      const heroHeight = this.$refs.hero.clientHeight
      const resultsHeight = this.$refs.searchResults.clientHeight
      this.helpTopMargin = resultsHeight - heroHeight + 125
    },

    resetSearch() {
      const url = `${window.location.origin}${window.location.pathname}`
      window.location = url
    },

    slotInCart(slotToSearch) {
      const slotId = slotToSearch.Id
      if (this.cart && slotToSearch) {
        const slot = this.cart.Items.find((item) => {
          const slotIdAttr = item.Attributes.find((attr) => attr.name === 'Slot Id')
          if (slotIdAttr) {
            const matchingItem = parseInt(slotIdAttr.value) === slotId
            if (matchingItem) {
              slotToSearch.shoppingCartItemId = item.Id
            }
            // Only set the cart has slots attribute one time. Future checks wont set it to false
            if (!this.cartHasSlots) {
              this.cartHasSlots = !!matchingItem
            }
            return matchingItem
          }
          return false
        })
        return !!slot
      }

      return false
    },

    slotSelected(slot) {
      const theSlot = this.checkInSlots.find((s) => s.Id === slot.Id)
      return !!theSlot
    },

    async toggleSlot(reservation, slot, forceCheckin) {
      const slotInCart = this.slotInCart(slot)
      const slotExists = slotInCart ? true : this.checkInSlots.find((s) => s.Id === slot.Id)

      if (!slotExists) {
        this.selectedReservation = reservation
        this.checkinCourseIdAttribute.SelectedValue = this.courseId
        this.checkinDateAttribute.SelectedValue = this.reservationDate.format('YYYY-MM-DD')
        this.checkinDateAttribute.SelectedDay = this.reservationDate.date()
        this.checkinDateAttribute.SelectedMonth = this.reservationDate.month() + 1
        this.checkinDateAttribute.SelectedYear = this.reservationDate.year()
        this.checkinDateAttribute.IsDate = true
        this.checkinDateAttribute.IsDate = true
        this.checkinEntitlementIdAttribute.SelectedValue = slot.EntitlementId
        this.checkinFeeIdAttribute.SelectedValue = slot.FeeId
        this.checkinFeeNameAttribute.SelectedValue = slot.FeeName
        this.checkinFeeOptionsAttribute.SelectedValue = ''
        this.checkinHolesAttribute.SelectedValue = reservation.NumberOfHolesValue
        this.checkinReservationPriceAttribute.SelectedValue = this.checkinPriceAttribute.SelectedValue
        this.checkinSlotIdAttribute.SelectedValue = slot.Id
        this.checkinSlotNameAttribute.SelectedValue = slot.LastName
        this.checkinSlotNumberAttribute.SelectedValue = slot.ResourceNumber
        this.checkinTimeAttribute.SelectedValue = reservation.Time
        this.checkinForceCheckinAttribute.SelectedValue = forceCheckin

        let wasSuccessful = await this.addSlotToCart(slot, forceCheckin)
        if (wasSuccessful === true) {
          this.checkInSlots = [...this.checkInSlots, slot]
        }
      } else {
        this.removingSlots = [...this.removingSlots, slot.Id]
        await this.removeItem({
          itemId: slot.shoppingCartItemId,
          removeFromLocalCart: false,
        })
        slot.State = 'NotCheckedIn'
        const filteredSlots = this.removingSlots.filter((s) => s !== slot.Id)
        this.removingSlots = filteredSlots

        const removeSlots = this.checkInSlots.filter((s) => s.Id !== slot.Id)
        this.$set(this, 'checkInSlots', removeSlots)
      }
    },
    ...mapActions({
      addToCart: 'cart/addToCart',
      fetchCartItems: 'cart/fetchCartItems',
      removeItem: 'cart/removeItemFromCart',
    }),
  },
  filters: {
    currency(n) {
      // TODO: Make this locale specific
      // This formatting will remove the cents (decimals) if zero
      return numeral(n).format('$0,0[.]00')
    },
  },
}
</script>

<style lang="scss">
#commerce-store {
  .tee-time-check-in {
    position: relative;

    .spinner-wrapper {
      @media screen and (max-width: 46em) {
        top: 20%;
      }
    }

    #hero {
      position: relative;
      #hero-image {
        position: relative;
        background-image: url('../../../Content/images/default-course-background.jpg');
        background-size: cover;
        background-repeat: no-repeat;
        min-height: 500px;
      }
      #dynamic-hero {
        position: relative;
        transition: background-image 200ms ease-in;
        min-height: 500px;
        &:after {
          position: absolute;
          background: rgba(0, 0, 0, 0.2);
          content: '';
          top: 0;
          bottom: 0;
          left: 0;
          right: 0;
          height: 500px;
        }
      }
      h1 {
        font-family: bebas-neue-by-fontfabric, Lato, Helvetica, sans-serif;
        font-size: 98px;
        font-weight: bold;
        color: white;
        position: absolute;
        bottom: 2rem;
        left: 5rem;
        opacity: 1;
        transition: opacity 150ms ease-in;
      }

      @media screen and (max-width: 768px) {
        h1 {
          font-size: 78px;
        }
      }

      @media screen and (max-width: 46em) {
        &.full-hero {
          height: 535px;
        }

        h1 {
          font-size: 54px;
          left: 2rem;
        }
      }
    }

    &.reservation-loaded {
      #hero {
        h1 {
          opacity: 0;
        }
      }

      section.help-content {
        transition: 100ms margin-top;
      }
    }

    section {
      &.search-reservation {
        padding: 2rem 3rem;
        background-color: #1e2744;

        .search-reservation__fields {
          display: flex;
          justify-content: space-around;
          align-items: flex-end;
          max-width: 1400px;
          width: 100%;
        }

        select {
          padding-left: 0;
        }

        input {
          padding-left: 5px;
        }

        .search-button {
          min-width: 220px;
        }

        @media screen and (max-width: 1200px) {
          .search-button,
          input {
            width: 180px;
          }
          .ax--select,
          select {
            width: 260px;
          }
          .search-button {
            width: 150px;
          }
        }

        @media screen and (max-width: 768px) {
          padding: 2rem;

          .search-reservation__fields {
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
            > * {
              width: 100%;
            }
          }
          .ax--select,
          .search-button,
          select,
          input {
            width: 100%;
          }
          .search-button {
            margin-top: 1.5rem;
          }
        }
      }
      &.help-content {
        margin: 4rem auto;
        min-height: 50vh;
        width: 80vw;
        max-width: 1400px;

        h2 {
          font-size: 2.375rem;
          margin-bottom: 0.5rem;
          font-family: inherit;
          font-weight: 500;
          line-height: 1.2;
          color: inherit;
          width: 100%;
        }

        #maincontent {
          display: flex;
          flex-wrap: wrap;

          .content-block {
            flex-basis: 33%;
            min-width: 350px;
            max-width: 450px;
            display: block;
            padding: 0 1.5rem;
          }
        }

        .redlinerule,
        .bluelinerule {
          width: 5.125rem;
          height: 0.1875rem;
          margin: 1.875rem 0;
          border: 0;
        }

        .bluelinerule {
          background: #029fdd;
        }
        .redlinerule {
          background: #e51937;
        }

        .intro-body {
          font-weight: 100;
          font-size: 1.35rem;
          line-height: 2.125rem;
          color: #0b1f66;
          letter-spacing: 0;
        }
      }
    }

    .search-results,
    .early-or-late,
    .no-result {
      position: absolute;
      width: 80vw;
      max-width: 1400px;
      top: 11rem;
      max-width: 650px;
      left: 50vw;
      transform: translateX(-50%);
      width: 90vw;
      z-index: 8;
      background: rgba(255, 255, 255, 0.9);
      padding: 1.5rem 2rem;

      @media screen and (max-width: 768px) {
        top: 380px;
      }

      @media screen and (max-width: 46em) {
        top: 22rem;
        left: 15px;
        right: 15px;
        transform: none;
      }

      h2 {
        font-size: 1.6rem;
        margin-top: 0;
        margin-bottom: 1rem;
      }

      p {
        line-height: 2.125rem;
        font-size: 1.2rem;
        margin-bottom: 1.5rem;
      }

      p.pendingCheckInMessage {
        font-size: 0.75em;
        text-align: center;
        line-height: 1.4;
        margin-top: 5px;
        margin-bottom: 5px;

        a {
          cursor: pointer;
        }
      }
    }

    .search-results {
      top: 220px;
      left: 50vw;
      transform: translateX(-50%);
      max-width: none;
    }

    .search-results {
      @media screen and (max-width: 768px) {
        top: 400px;
        max-width: 550px;
      }

      @media screen and (max-width: 46em) {
        top: 340px;
        padding: 15px 10px;
        width: auto;
        max-width: none;
        left: 10px;
        right: 10px;
        transform: none;
      }

      &__header {
        display: flex;
        justify-content: space-between;
        font-size: 1.3rem;
        text-transform: capitalize;
      }

      &__list {
        margin-top: 1.5rem;
        .player-slot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 1.125rem;
          background-color: #c0dde8;
          margin-bottom: 1.5rem;
          padding: 0.75rem 1rem;

          @media screen and (max-width: 768px) {
            flex-direction: column;
            justify-content: flex-start;
            align-items: center;

            .player-slot__date,
            .player-slot__number,
            .player-slot__action {
              flex-basis: 64px;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              width: 100%;
            }

            .player-slot__action {
              .ax--btn {
                padding: 1rem 0.5rem;
                height: auto;
                max-height: none;
                width: 100%;
                max-width: 340px;
              }
            }
          }

          &__course {
            flex: 1;
          }

          &__date,
          &__number {
            flex-basis: 180px;
            text-align: center;
          }

          &__action {
            flex-basis: 230px;
            text-align: right;
            .ax--btn {
              width: 100%;
            }
            .ax--btn.slot--pendingCheckIn {
              opacity: 1;
              color: darken(#3588cb, 15);
              background: repeating-linear-gradient(
                45deg,
                rgba(#3588cb, 0.2),
                rgba(#3588cb, 0.2) 10px,
                #cddeeb 10px,
                #cddeeb 20px
              );
            }
            .ax--btn.slot--checkedIn {
              opacity: 1;
              color: #0c2e60;
              background: rgba(0, 0, 0, 0.1);
              border-color: rgba(0, 0, 0, 0.1);
            }
          }
        }
      }

      &__continue {
        display: flex;
        justify-content: center;

        .ax--btn {
          min-width: 200px;
        }
      }
    }
    .ax--label {
      color: white;
      text-transform: capitalize;
      font-size: 0.85em;
      margin-bottom: 0.5em;
      line-height: 1;
    }
    input[type='text'].ax--input {
      background: transparent;
      border: none;
      border-bottom: 1px solid white;
      font-size: 1.5rem !important;
      font-weight: normal;
      color: white;

      &.error {
        border-color: red;
        border-bottom-width: 2px;
      }

      &::placeholder {
        color: white;
        font-size: 1.5rem;
        opacity: 0.65;
        font-weight: normal;
      }
    }
    .ax--select {
      select {
        background: transparent;
        color: white;
        border: none;
        border-bottom: 1px solid white;
        font-size: 1.5rem !important;
      }
      .ax--select__arrow {
        fill: white;
      }
      .ax--select-option {
        color: black !important;
      }
      &.error {
        select {
          border-color: red;
          border-bottom-width: 2px;
        }
      }
    }
  }
}
</style>
